import { useState, useRef } from 'react'
import { Upload } from 'lucide-react'
import { useAuth } from '../../../context/AuthContext'
import * as tus from 'tus-js-client'

export default function VideoUpload({ onComplete, setStatus }) {
  const { api } = useAuth()
  const fileRef = useRef(null)
  const [uploadTitle, setUploadTitle] = useState('')
  const [uploadCreator, setUploadCreator] = useState('')
  const [uploading, setUploading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [uploadStatus, setUploadStatus] = useState('')

  function formatBytes(bytes) {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  async function handleUpload() {
    const file = fileRef.current?.files?.[0]
    if (!file) {
      alert('Please choose a file')
      return
    }

    setUploading(true)
    setProgress(0)
    setUploadStatus('Preparing upload...')

    try {
      const init = await api('/upload-url', {
        method: 'POST',
        body: JSON.stringify({
          title: uploadTitle.trim() || file.name,
          creator: uploadCreator.trim()
        })
      })

      const uploadURL = init.result?.uploadURL || init.uploadURL
      const uid = init.result?.uid || init.uid

      if (!uploadURL) {
        throw new Error('No upload URL received')
      }

      console.log('Upload URL:', uploadURL)

      // Check if this is a modern TUS endpoint or legacy endpoint
      const isModernTus = uploadURL.includes('videodelivery.net/tus') || uploadURL.includes('tusv2=true')
      const isLegacy = uploadURL.includes('upload.cloudflarestream.com')

      if (isLegacy) {
        // Use simple FormData upload for legacy endpoint
        console.log('Using legacy FormData upload')
        setUploadStatus('Uploading (legacy mode)...')
        
        const formData = new FormData()
        formData.append('file', file)
        
        const xhr = new XMLHttpRequest()
        
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const pct = ((e.loaded / e.total) * 100).toFixed(1)
            setProgress(pct)
            setUploadStatus(`Uploading: ${pct}% (${formatBytes(e.loaded)} / ${formatBytes(e.total)})`)
          }
        })
        
        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            setUploadStatus('✅ Upload complete! Processing video...')
            setUploading(false)
            setProgress(0)
            if (fileRef.current) fileRef.current.value = ''
            setUploadTitle('')
            setUploadCreator('')
            onComplete(uid)
          } else {
            setUploadStatus(`Error: Upload failed (${xhr.status})`)
            setUploading(false)
          }
        })
        
        xhr.addEventListener('error', () => {
          setUploadStatus('Error: Upload failed')
          setUploading(false)
        })
        
        xhr.open('POST', uploadURL)
        xhr.send(formData)
        
      } else {
        // Use TUS for modern endpoint
        console.log('Using TUS upload')
        
        const sanitizedFilename = file.name
          .replace(/[^\w\s.-]/g, '_')
          .substring(0, 100)

        const upload = new tus.Upload(file, {
          uploadUrl: uploadURL,
          chunkSize: 50 * 1024 * 1024,
          retryDelays: [0, 1000, 3000, 5000, 10000],
          metadata: {
            filename: sanitizedFilename,
            filetype: file.type || 'video/mp4'
          },
          onError: (error) => {
            console.error('Upload error:', error)
            setUploadStatus(`Error: ${error.message}`)
            setUploading(false)
          },
          onProgress: (bytesUploaded, bytesTotal) => {
            const pct = ((bytesUploaded / bytesTotal) * 100).toFixed(1)
            setProgress(pct)
            setUploadStatus(`Uploading: ${pct}% (${formatBytes(bytesUploaded)} / ${formatBytes(bytesTotal)})`)
          },
          onSuccess: () => {
            setUploadStatus('✅ Upload complete! Processing video...')
            setUploading(false)
            setProgress(0)
            if (fileRef.current) fileRef.current.value = ''
            setUploadTitle('')
            setUploadCreator('')
            onComplete(uid)
          }
        })

        upload.start()
      }
    } catch (e) {
      console.error('Upload init error:', e)
      setUploadStatus(`Error: ${e.message}`)
      setUploading(false)
    }
  }

  return (
    <div className="video-upload">
      <h3>
        <Upload size={18} style={{ marginRight: 8, verticalAlign: 'middle' }} />
        Upload New Video
      </h3>

      <div className="upload-form">
        <div className="upload-row">
          <input
            ref={fileRef}
            type="file"
            accept="video/*"
            disabled={uploading}
          />
          <input
            type="text"
            placeholder="Title (optional)"
            value={uploadTitle}
            onChange={e => setUploadTitle(e.target.value)}
            disabled={uploading}
          />
          <input
            type="text"
            placeholder="Creator (optional)"
            value={uploadCreator}
            onChange={e => setUploadCreator(e.target.value)}
            disabled={uploading}
          />
          <button onClick={handleUpload} disabled={uploading}>
            {uploading ? 'Uploading...' : 'Start Upload'}
          </button>
        </div>

        {uploading && (
          <div className="upload-progress">
            <progress value={progress} max="100" />
          </div>
        )}

        {uploadStatus && (
          <div className="upload-status">{uploadStatus}</div>
        )}
      </div>
    </div>
  )
}